/**
 * Mirroring the on-device focus log to the backend.
 *
 * Pure functions only; `hooks/focus/useFocusLogSync` runs them. The model:
 *
 * - The browser log stays the source the page reads. Every entry not yet
 *   confirmed (`synced` unset) is an upload waiting to happen, which is what
 *   makes this an offline outbox AND the one-time upload of a log written
 *   before sync existed - those entries simply have never been synced.
 * - Uploads are idempotent on the server, keyed by the block id, so a replay
 *   or a second tab sending the same block changes nothing.
 * - An entry is marked synced only if it still matches what was sent: a recap
 *   typed while the request was in flight keeps it pending for the next round.
 * - Blocks from other devices merge in; a local entry with unsent changes wins
 *   over the server's older copy of it.
 */
import type { FocusBlockRecord, FocusBlockUpload } from '@/types/api';
import { LOG_RETENTION_MS, type FocusLogEntry } from '@/lib/focus/focusLog';

// The server's limits (`backend/src/wellbeing/schemas.py`). Clamping to them
// before sending means a real block is never refused as malformed.
export const MAX_INTENTION_LENGTH = 200;
export const MAX_RECAP_LENGTH = 500;
export const MAX_LESSON_ID_LENGTH = 64;
export const MAX_FOCUS_SECONDS = 12 * 60 * 60;
export const MAX_BLOCKS_PER_SYNC = 100;

const clip = (text: string | undefined, max: number) =>
  text ? text.slice(0, max) : undefined;

/** The entries still to send, oldest first. */
export function pendingUploads(log: FocusLogEntry[]): FocusLogEntry[] {
  return log.filter(e => !e.synced && !e.rejected);
}

export function toUpload(entry: FocusLogEntry): FocusBlockUpload {
  const focusSeconds = Math.min(
    Math.max(0, Math.round(entry.focusMs / 1000)),
    MAX_FOCUS_SECONDS
  );
  const endedAt = Math.max(
    entry.endedAt ?? entry.startedAt + entry.focusMs,
    entry.startedAt
  );
  const upload: FocusBlockUpload = {
    id: entry.id,
    started_at: new Date(entry.startedAt).toISOString(),
    ended_at: new Date(endedAt).toISOString(),
    focus_seconds: focusSeconds,
    ended_early: entry.endedEarly ?? false,
  };
  const intention = clip(entry.intention, MAX_INTENTION_LENGTH);
  const lessonId = clip(entry.lessonId, MAX_LESSON_ID_LENGTH);
  const recap = clip(entry.recap, MAX_RECAP_LENGTH);
  if (intention) upload.intention = intention;
  if (lessonId) upload.lesson_id = lessonId;
  if (recap) upload.recap = recap;
  return upload;
}

const sameUpload = (a: FocusBlockUpload, b: FocusBlockUpload) =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Mark as `flag` the entries the server answered for, but only those that
 * still match what was sent. Returns `log` itself when nothing changed.
 */
export function markSent(
  log: FocusLogEntry[],
  sent: FocusBlockUpload[],
  ids: readonly string[],
  flag: 'synced' | 'rejected'
): FocusLogEntry[] {
  const answered = new Set(ids);
  const byId = new Map(sent.map(u => [u.id, u]));
  let changed = false;
  const next = log.map(entry => {
    const upload = byId.get(entry.id);
    if (!upload || !answered.has(entry.id)) return entry;
    if (!sameUpload(toUpload(entry), upload)) return entry;
    changed = true;
    return { ...entry, [flag]: true };
  });
  return changed ? next : log;
}

export function fromRecord(record: FocusBlockRecord): FocusLogEntry {
  const startedAt = Date.parse(record.started_at);
  const entry: FocusLogEntry = {
    id: record.id,
    startedAt,
    endedAt: Date.parse(record.ended_at),
    focusMs: record.focus_seconds * 1000,
    endedEarly: record.ended_early,
    synced: true,
  };
  if (record.intention) entry.intention = record.intention;
  if (record.lesson_id) entry.lessonId = record.lesson_id;
  if (record.recap) entry.recap = record.recap;
  return entry;
}

/**
 * Fold the server's blocks into the local log: union by id, unsent local
 * changes win, past-retention entries drop, oldest first. Returns `log`
 * itself when the merge changes nothing, so the caller can skip a write.
 */
export function mergeRemote(
  log: FocusLogEntry[],
  remote: FocusBlockRecord[],
  now: number
): FocusLogEntry[] {
  const merged = new Map(log.map(e => [e.id, e]));
  for (const record of remote) {
    const local = merged.get(record.id);
    if (local && !local.synced) continue;
    merged.set(record.id, fromRecord(record));
  }
  const next = [...merged.values()]
    .filter(e => now - e.startedAt < LOG_RETENTION_MS)
    .sort((a, b) => a.startedAt - b.startedAt);
  const unchanged =
    next.length === log.length &&
    next.every((e, i) => JSON.stringify(e) === JSON.stringify(log[i]));
  return unchanged ? log : next;
}

/** Where to start the pull: the same 90 days the browser keeps. */
export function syncWindowStart(now: number): string {
  return new Date(now - LOG_RETENTION_MS).toISOString();
}
